import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { RadioProvider } from "@/contexts/RadioContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageLoader } from "@/components/PageLoader";
import { FeatureGate } from "@/components/FeatureGate";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import Login from "./pages/Login";

const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const ManualPayment = lazyWithRetry(() => import("./pages/ManualPayment"));
const ArkOSConnection = lazyWithRetry(() => import("./pages/ArkOSConnection"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Academy = lazyWithRetry(() => import("./pages/Academy"));
const AcademyCourse = lazyWithRetry(() => import("./pages/AcademyCourse"));
const LessonReader = lazyWithRetry(() => import("./pages/LessonReader"));
const Practice = lazyWithRetry(() => import("./pages/Practice"));
const Shop = lazyWithRetry(() => import("./pages/Shop"));
const Leaderboard = lazyWithRetry(() => import("./pages/Leaderboard"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const ContestView = lazyWithRetry(() => import("./pages/ContestView"));
const AdminContestView = lazyWithRetry(() => import("./pages/AdminContestView"));
const Collection = lazyWithRetry(() => import("./pages/Collection"));
const Studio = lazyWithRetry(() => import("./pages/admin/Studio"));
const StudioCourseDetail = lazyWithRetry(() => import("./pages/admin/StudioCourseDetail"));
const LessonBuilder = lazyWithRetry(() => import("./pages/admin/LessonBuilder"));
const KnowledgeLibrary = lazyWithRetry(() => import("./pages/admin/KnowledgeLibrary"));
const KnowledgeLibraryDetail = lazyWithRetry(() => import("./pages/admin/KnowledgeLibraryDetail"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="dark" storageKey="musicquizizz-theme">
      <RadioProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                <Route path="/admin/contest/:id" element={<ProtectedRoute><AdminContestView /></ProtectedRoute>} />
                <Route path="/admin/studio" element={<ProtectedRoute><Studio /></ProtectedRoute>} />
                <Route path="/admin/studio/:courseId" element={<ProtectedRoute><StudioCourseDetail /></ProtectedRoute>} />
                <Route path="/admin/studio/:courseId/lessons/:lessonId" element={<ProtectedRoute><LessonBuilder /></ProtectedRoute>} />
                <Route path="/admin/knowledge" element={<ProtectedRoute><KnowledgeLibrary /></ProtectedRoute>} />
                <Route path="/admin/knowledge/:libraryId" element={<ProtectedRoute><KnowledgeLibraryDetail /></ProtectedRoute>} />
                <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
                <Route path="/manual-payment" element={<ProtectedRoute><ManualPayment /></ProtectedRoute>} />
                <Route path="/arkos-connection" element={<ProtectedRoute><ArkOSConnection /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/academy" element={<ProtectedRoute><FeatureGate flag="academy"><Academy /></FeatureGate></ProtectedRoute>} />
                <Route path="/academy/c/:slug" element={<ProtectedRoute><FeatureGate flag="academy"><AcademyCourse /></FeatureGate></ProtectedRoute>} />
                <Route path="/academy/c/:slug/l/:lessonId" element={<ProtectedRoute><FeatureGate flag="academy"><LessonReader /></FeatureGate></ProtectedRoute>} />
                <Route path="/practice" element={<ProtectedRoute><Practice /></ProtectedRoute>} />
                <Route path="/shop" element={<ProtectedRoute><FeatureGate flag="shop"><Shop /></FeatureGate></ProtectedRoute>} />

                <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
                <Route path="/contest/:id" element={<ProtectedRoute><ContestView /></ProtectedRoute>} />
                <Route path="/collection" element={<ProtectedRoute><Collection /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </RadioProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
